/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Grid, Paper, Typography, colors } from "@mui/material"
import { graphql } from "../../../gql"
import { useNavigate, useParams } from "react-router-dom"
import { useLazyQuery, useMutation } from "@apollo/client"
import HeaderAdd from "../../Common/HeaderAdd/HeaderAdd"
import { useCallback, useEffect, useMemo, useState } from "react"
import Loader from "../../Common/Loader/Loader"
import { setTitle } from "../../../store/features/menu/menuSlice"
import { useAppDispatch } from "../../../store/app/hooks"
import NoDataStatistic from "../../Common/NoDataStatistic/NoDataStatistic"
import Chart from "../../Common/Chart/Chart"
import StarRating from "./StarRating/StarRating"
import { havePermissions, isSuperAdmin } from "../../../utils/permissions"
import ToggleSwitch from "../../Admin/FormAdmin/TableRoles/SwitchButton/SwitchButton"
import { setOpenSnackbar } from "../../../store/features/snackbar/snackbarSlice"
import { getUserCountryId } from "../../../utils/authToken"
import useLang from "../../../hooks/useLang"
import ListOrder from "../../Order/ListOrder/ListOrder"

const GET_CRAFTMAN = graphql(`
  query GetDetailCraftmanQuery(
    $where: UserWhereUniqueInput!
    $aggregateOrderWhere2: OrderWhereInput
    $aggregateNoticeOrderWhere2: NoticeOrderWhereInput
  ) {
    user(where: $where) {
      id
      phoneNumber
      email
      role
      name
      lastName
      lang
      active
      yearOfExperience
      ProfilePhoto {
        id
        url
      }
      PhotoAchievements {
        id
        name
        url
      }
      Categories {
        id
        name_fr
        name_en
      }
      ItemTypes {
        id
        name_fr
        name_en
      }
      MaterialTypes {
        id
        name_fr
        name_en
      }
      country {
        id
        name_fr
        currency
        name_en
      }
    }
    aggregateOrder(where: $aggregateOrderWhere2) {
      _count {
        _all
      }
    }
    aggregateNoticeOrder(where: $aggregateNoticeOrderWhere2) {
      _count {
        _all
      }
      _avg {
        note
      }
    }
  }
`)

const updateCraftmanMutation = graphql(`
  mutation UpdateOneUser(
    $data: UserUpdateInput!
    $where: UserWhereUniqueInput!
  ) {
    updateOneUser(data: $data, where: $where) {
      id
      active
    }
  }
`)

interface Props {
  craftmanId?: number
}

const DetailCraftman = ({ craftmanId }: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const params = useParams()
  const { getText, getByLang } = useLang()
  const [getCraftman, { data, loading }] = useLazyQuery(GET_CRAFTMAN)
  const [active, setActive] = useState(false)
  const [updatecraftman] = useMutation(updateCraftmanMutation)
  const colorsGlobal = [colors.blue[400], colors.green[400]]

  const id = useMemo(
    () => (craftmanId ? craftmanId : params.id ? parseInt(params.id, 10) : 0),
    [craftmanId, params],
  )

  useEffect(() => {
    getCraftman({
      variables: {
        where: {
          id,
        },
        aggregateNoticeOrderWhere2: {
          Order: {
            is: {
              craftmanId: {
                equals: id,
              },
            },
          },
        },
        aggregateOrderWhere2: {
          craftmanId: {
            equals: id,
          },
        },
      },
      onCompleted(data) {
        if (!isSuperAdmin()) {
          if (data.user?.country?.id != getUserCountryId()) {
            navigate("/")
            return
          }
        }
        setActive(data.user?.active as boolean)
      },
    })
  }, [id])

  const handleEdit = (id: number) => {
    dispatch(setTitle(getText("Modifier un artisan")))
    navigate(`/craftman/edit/${id}`)
  }

  const handleWallet = (id: number) => {
    dispatch(setTitle(getText("Cagnotte artisan")))
    navigate(`/craftman/wallet/${id}`)
  }

  const getStatistic = useCallback(() => {
    const value_order = data?.aggregateOrder?._count?._all || 0
    const value_rate = data?.aggregateNoticeOrder?._count?._all || 0
    return !value_order && !value_rate
      ? []
      : [
          {
            label: getText("Nombre commande"),
            value: value_order,
            color: colorsGlobal[0],
          },
          {
            label: getText("Nombre avis"),
            value: value_rate,
            color: colorsGlobal[1],
          },
        ]
  }, [data, getText])

  const handleAddOrder = () => {
    dispatch(setTitle(getText("Ajouter une commande")))
    navigate(`/order/add`)
  }

  const handleChange = () => {
    setActive(!active)
    updatecraftman({
      variables: {
        data: {
          active: {
            set: !active,
          },
        },
        where: {
          id,
        },
      },
      update(cache, { errors, data }) {
        if (!errors && data) {
          const active = data.updateOneUser?.active
          cache.modify({
            id: cache.identify({
              __typename: "User",
              id,
            }),
            fields: {
              active(existing = false) {
                return existing != active ? active : existing
              },
            },
          })
        }
      },
      onCompleted(data) {
        dispatch(
          setOpenSnackbar({
            message: getText(
              "La disponibilité de l'artisan a été mise à jour avec succès.",
            ),
            status: "success",
          }),
        )
      },
      onError: (error) => {
        setActive(!active)
        dispatch(
          setOpenSnackbar({
            message: getText(
              "L'action a été refusée. Veuillez réessayer s'il vous plaît.",
            ),
          }),
        )
      },
    })
  }

  if (loading) return <Loader />

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <HeaderAdd
                    title={getText("Information de l'artisan")}
                    variant="h4"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Typography>
                        <b>ID:</b> {data?.user?.id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>
                        <b>{getText("Prénom")}:</b> {data?.user?.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>
                        <b>{getText("Catégorie")}:</b>{" "}
                        {data?.user?.Categories.flatMap((el) =>
                          getByLang({
                            value_fr: el.name_fr,
                            value_en: el.name_en,
                          }),
                        ).toString()}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      direction={"row"}
                      display={"flex"}
                      alignItems={"normal"}
                    >
                      <Typography mr={1}>
                        <b>{getText("Avis des utilisateurs")}: </b>{" "}
                      </Typography>
                      <StarRating
                        note={data?.aggregateNoticeOrder?._avg?.note || 1}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      direction={"row"}
                      display={"flex"}
                      alignItems={"normal"}
                    >
                      <Box>
                        <Typography mr={1}>
                          <b>{getText("Disponibilité")}: </b>{" "}
                        </Typography>
                      </Box>
                      <Box>
                        <ToggleSwitch
                          checked={active}
                          handleChange={handleChange}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container mt={3}>
                    <Grid
                      item
                      xs={12}
                      display={"flex"}
                      justifyContent={"flex-end"}
                    >
                      {havePermissions(["craftman.change"]) && (
                        <Button
                          onClick={() => handleEdit(data?.user?.id || 0)}
                          sx={{ mr: 1 }}
                          color="info"
                          variant="outlined"
                          size="small"
                        >
                          {getText("Modifier")}
                        </Button>
                      )}
                      {havePermissions(["craftman.view"]) && (
                        <Button
                          onClick={() => handleWallet(data?.user?.id || 0)}
                          sx={{ mr: 1 }}
                          color="warning"
                          variant="outlined"
                          size="small"
                        >
                          {getText("Voir sa cagnotte")}
                        </Button>
                      )}
                      {havePermissions(["order.create"]) && (
                        <Button
                          onClick={handleAddOrder}
                          color="success"
                          variant="outlined"
                          size="small"
                        >
                          {getText("Ajouter une commande")}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 2,
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <HeaderAdd
                    title={getText("Statistique de l'artisan")}
                    variant="h4"
                  />
                </Grid>
                <Grid item xs={12}>
                  {getStatistic().length ? (
                    <Chart data={getStatistic()} />
                  ) : (
                    <NoDataStatistic />
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid container>
            <Grid item xs={12}>
              <HeaderAdd title={getText("Liste des commandes")} variant="h4" />
            </Grid>
            <Grid item xs={12} mt={2}>
              <ListOrder
                isOnlyCraftman
                extraQuery={{
                  craftmanId: {
                    equals: id,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  )
}

export default DetailCraftman
